import { useEffect, useState } from "react"
import { Button, Descriptions, Flex, Modal, Spin } from "antd"
import { getStaffOrderOpDetail } from "~/api/staff"
import { Alert } from "~/components/Alert"
import { formatAmount, formatDateTime, isAbortError } from "~/common/utils"
import { BulkWalletEntryMode, OrderType } from "~/common/constants"
import { bulkEntryModeLabel } from "./labels"
import { getStaffBulkWalletAmount } from "./orderForm"
import {
  OrderDetails,
  PaymentDetails,
  RechargeDetails,
} from "~/pages/Staff/OrderCleanup/Details"

export const OrderLogDetailsModal = ({
  opLogId,
  onClose,
}: {
  opLogId: string
  onClose: () => void
}) => {
  const [detail, setDetail] = useState<API.Staff.StaffOrderOpDetail | null>(
    null,
  )
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    const controller = new AbortController()
    const load = async () => {
      setLoading(true)
      setFailed(false)
      setDetail(null)
      try {
        const response = await getStaffOrderOpDetail(
          { op_log_id: opLogId },
          controller.signal,
        )
        if (!controller.signal.aborted) {
          setDetail(response.data ?? null)
          setFailed(!response.data)
        }
      } catch (error) {
        if (!isAbortError(error) && !controller.signal.aborted) setFailed(true)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    void load()
    return () => controller.abort()
  }, [opLogId, retry])
  return (
    <Modal
      open
      title="订单录入详情"
      width={1100}
      onCancel={onClose}
      footer={<Button onClick={onClose}>关闭</Button>}
    >
      {loading ? (
        <Flex justify="center">
          <Spin />
        </Flex>
      ) : failed ? (
        <Alert
          level="strong"
          showIcon
          title="录入详情加载失败"
          action={
            <Button onClick={() => setRetry((value) => value + 1)}>
              重新加载
            </Button>
          }
        />
      ) : detail ? (
        <Flex
          vertical
          gap={20}
        >
          <Descriptions
            bordered
            size="small"
            column={2}
            title="Staff 操作记录"
          >
            <Descriptions.Item label="操作记录ID">
              {detail.op_log.id}
            </Descriptions.Item>
            <Descriptions.Item label="Staff ID">
              {detail.op_log.staff_id}
            </Descriptions.Item>
            <Descriptions.Item label="用户账号">
              {detail.op_log.user_mobile}
            </Descriptions.Item>
            <Descriptions.Item label="录入交易号">
              {detail.op_log.external_payment_no}
            </Descriptions.Item>
            <Descriptions.Item label="录入时间">
              {formatDateTime(detail.op_log.created_t)}
            </Descriptions.Item>
            {detail.op_log.order_type === OrderType.bulkWalletPackage ? (
              <>
                <Descriptions.Item label="录入方式">
                  {
                    bulkEntryModeLabel[
                      detail.op_log.bulk_entry_mode ??
                        BulkWalletEntryMode.quantity
                    ]
                  }
                </Descriptions.Item>
                <Descriptions.Item label="实付金额">
                  {formatAmount(getStaffBulkWalletAmount(detail.op_log))}
                </Descriptions.Item>
                <Descriptions.Item label="购买数量">
                  {detail.op_log.wallet_recharge_amount} 积点
                </Descriptions.Item>
                <Descriptions.Item label="购买单价">
                  {detail.op_log.purchase_unit_price} 分/积点
                </Descriptions.Item>
                <Descriptions.Item label="退款结算单价">
                  {detail.op_log.refund_unit_price} 分/积点
                </Descriptions.Item>
              </>
            ) : null}
          </Descriptions>
          {detail.order ? (
            <OrderDetails order={detail.order} />
          ) : (
            <Alert
              level="weak"
              title="该录入记录尚未关联订单"
            />
          )}
          <PaymentDetails payment={detail.payment_trade} />
          <RechargeDetails records={detail.wallet_recharge_records} />
        </Flex>
      ) : null}
    </Modal>
  )
}
